import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { X, Target, Loader2, Flame, Send } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DailyChallengeModal = ({ token, onClose }) => {
  const { t, lang } = useLang();
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [data, setData] = useState(null);
  const [response, setResponse] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [streak, setStreak] = useState(0);
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    axios.get(`${API}/daily-challenge?lang=${lang}`, auth)
      .then(({ data }) => {
        setData(data);
        setStreak(data.streak || 0);
        setDone(!!data.done);
        if (data.done) {
          setFeedback(data.feedback);
          setResponse(data.your_response || "");
        }
      })
      .catch((e) => { console.error("load daily challenge failed:", e); setData({ scenario: "", prompt: "" }); });
  }, []); // eslint-disable-line -- run once on mount; auth token is stable per session

  const submit = async () => {
    const r = response.trim();
    if (!r) { toast.error(t.daily_empty); return; }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/daily-challenge/respond`, { response: r, lang }, auth);
      setFeedback(data.feedback);
      setStreak(data.streak || 0);
      setDone(true);
    } catch (e) { console.error("daily challenge respond failed:", e); toast.error(t.analyze_fail); }
    setSending(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto" data-testid="daily-modal">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg bg-[#F4F0E6] border-[4px] border-black rounded-2xl shadow-[8px_8px_0px_0px_#000] my-8"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b-[3px] border-black">
          <h2 className="font-display font-bold text-xl uppercase tracking-tight flex items-center gap-2">
            <Target size={20} strokeWidth={2.5} /> {t.daily_title}
          </h2>
          <div className="flex items-center gap-2">
            {streak > 0 && (
              <span className="flex items-center gap-1 bg-[#FF3366] text-white border-[2px] border-black rounded-full px-2.5 py-1 font-mono2 font-bold text-xs" data-testid="daily-streak">
                <Flame size={13} strokeWidth={2.5} /> {streak} {t.daily_streak}
              </span>
            )}
            <button data-testid="daily-close-btn" onClick={onClose} className="border-[2px] border-black rounded-full p-1 bg-white hover:bg-[#FFD500] transition-colors">
              <X size={18} strokeWidth={2.5} />
            </button>
          </div>
        </div>

        <div className="p-4 space-y-4">
          {data === null && (
            <div className="flex justify-center py-10"><Loader2 className="animate-spin" size={26} /></div>
          )}

          {data && (
            <>
              <p className="text-sm font-medium text-[#555]">{t.daily_sub}</p>

              {/* scenario */}
              <div className="border-[3px] border-black rounded-xl bg-white p-4 shadow-[4px_4px_0px_0px_#000]" data-testid="daily-scenario">
                <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#BD00FF] mb-2">{t.daily_scenario}</p>
                <p className="text-base font-semibold text-[#222] leading-relaxed">{data.scenario}</p>
                <p className="mt-3 text-sm font-bold text-black bg-[#FFD500] border-[2px] border-black rounded-lg px-3 py-2 inline-block">{data.prompt}</p>
              </div>

              {/* your turn / done */}
              {!done ? (
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#444] mb-2">{t.daily_your_turn}</p>
                  <textarea
                    data-testid="daily-input"
                    value={response}
                    onChange={(e) => setResponse(e.target.value)}
                    placeholder={t.daily_ph}
                    rows={3}
                    maxLength={1000}
                    className="w-full resize-none bg-white border-[3px] border-black rounded-xl p-3 text-base font-medium placeholder:text-[#999] focus:outline-none focus:ring-4 focus:ring-[#BD00FF]"
                  />
                  <button
                    data-testid="daily-submit-btn"
                    onClick={submit}
                    disabled={sending}
                    className="mt-3 w-full flex items-center justify-center gap-2 bg-[#00FFA3] border-[3px] border-black rounded-xl py-3 font-display font-bold uppercase tracking-tight text-lg shadow-[4px_4px_0px_0px_#000] transition-[transform,box-shadow] duration-100 hover:shadow-[2px_2px_0px_0px_#000] hover:translate-x-[2px] hover:translate-y-[2px] disabled:opacity-70"
                  >
                    {sending ? <><Loader2 className="animate-spin" size={18} /> {t.daily_loading}</> : <><Send size={18} strokeWidth={2.5} /> {t.daily_submit}</>}
                  </button>
                </div>
              ) : (
                <>
                  {response && (
                    <div className="border-[2px] border-black rounded-xl bg-white p-3" data-testid="daily-your-answer">
                      <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#999] mb-1">{t.daily_your_answer}</p>
                      <p className="text-sm font-semibold text-[#222]">"{response}"</p>
                    </div>
                  )}
                  {feedback && (
                    <div className="border-[3px] border-black rounded-xl bg-black text-white p-4 shadow-[4px_4px_0px_0px_#00FFA3]" data-testid="daily-feedback">
                      <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#00FFA3] mb-2">{t.daily_feedback_title}</p>
                      <p className="text-sm leading-relaxed font-medium">{feedback}</p>
                    </div>
                  )}
                  <p className="text-center text-[#666] text-sm font-semibold" data-testid="daily-come-back">✅ {t.daily_done} {t.daily_come_back}</p>
                </>
              )}
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};
